import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip';

import { cn } from '~/lib/utils';

type TooltipProviderProps = TooltipPrimitive.Provider.Props;
type TooltipProps = TooltipPrimitive.Root.Props;
type TooltipTriggerProps = TooltipPrimitive.Trigger.Props;

function TooltipProvider({ delay = 300, ...props }: TooltipProviderProps) {
  return <TooltipPrimitive.Provider delay={delay} {...props} />;
}

function Tooltip(props: TooltipProps) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
}

function TooltipTrigger(props: TooltipTriggerProps) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

type TooltipContentProps = TooltipPrimitive.Popup.Props &
  Pick<TooltipPrimitive.Positioner.Props, 'side' | 'align' | 'sideOffset' | 'alignOffset'>;

/**
 * Every icon-only trigger this wraps still needs its own `aria-label` - the
 * tooltip is a supplementary visual hint, not the accessible name.
 */
function TooltipContent({
  className,
  side = 'top',
  align = 'center',
  sideOffset = 6,
  alignOffset = 0,
  children,
  ...props
}: TooltipContentProps) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        side={side}
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        className="z-50 outline-none"
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={cn(
            'max-w-xs origin-[var(--transform-origin)] rounded-md bg-foreground px-2.5 py-1 text-xs font-medium text-background shadow-layer transition-[transform,scale,opacity] data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0',
            className,
          )}
          {...props}
        >
          {children}
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  );
}

export {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  type TooltipProviderProps,
  type TooltipProps,
  type TooltipTriggerProps,
  type TooltipContentProps,
};
